import dayjs from "dayjs";
import { Contour } from "./Contour";
import { Selectbox } from "./Form";
import { outputFilePrefix } from "./OutputName";
import { createViewerLink, openViewer } from "./OutputViewer";
import { Shade } from "./Shade";
import { readChoice, readFlag } from "./UrlState";
import { Wind } from "./Wind";

export type ImageFormat = "png" | "jpeg" | "webp";

export class Downloader {
    private readonly rasterizer = new Rasterizer();
    private readonly size = new Selectbox("#download_size");
    private readonly format = new Selectbox("#download_format");
    private readonly link = document.querySelector("#link_png")!;
    private readonly imageButton = document.querySelector("#download_image")!;
    private objectUrl: string | null = null;
    constructor(
        private readonly shade: Shade,
        private readonly wind: Wind,
        private readonly contour: Contour,
        private readonly params: URLSearchParams,
    ) {
        // #download_image (画像生成) のclickは、時間幅により静止画とZIPを
        // 振り分ける必要があるためApp側で束縛する
        this.size.onChange(() => this.updateCropGuide());
        document
            .querySelector("#cb_safety")
            ?.addEventListener("change", () => this.updateSafetyGuide());
        window.addEventListener("beforeunload", () => this.revokeObjectUrl());
        this.loadParams();
    }
    loadParams() {
        this.size.value = readChoice(
            this.params,
            "out_size",
            [
                "1920x1080",
                "1440x1080",
                "3840x2160",
                "1280x720",
                "960x540",
                "720x405",
            ] as const,
            "1920x1080",
        );
        this.format.value = readChoice(
            this.params,
            "out_format",
            ["png", "jpeg", "webp"] as const,
            "png",
        );
        const safety = readFlag(this.params, "out_safety", "0") === "1";
        const checkbox = document.querySelector<HTMLInputElement>("#cb_safety");
        if (checkbox) {
            checkbox.checked = safety;
        }
        this.updateCropGuide();
        this.updateSafetyGuide();
    }
    saveParams() {
        this.params.set("out_size", this.size.value);
        this.params.set("out_format", this.format.value);
        this.params.set(
            "out_safety",
            document.querySelector<HTMLInputElement>("#cb_safety")?.checked
                ? "1"
                : "0",
        );
    }
    async toDataURL() {
        const [width, height] = this.size.value.split("x").map(Number);
        try {
            await this.shade.toCaptureMode();
            await this.contour.toCaptureMode(width);
            await this.wind.toCaptureMode(width);
            this.rasterizer.setSize(width, height);
            await this.rasterizer.render();
            return this.rasterizer.toDataURL(this.format.value);
        } finally {
            await Promise.all([
                this.shade.toDisplayMode(),
                this.contour.toDisplayMode(),
                this.wind.toDisplayMode(),
            ]);
        }
    }
    getSelectedSize(): [number, number] {
        const [width, height] = this.size.value.split("x").map(Number);
        return [width, height];
    }
    getSelectedFormat(): ImageFormat {
        return this.format.value as ImageFormat;
    }
    async toBlob(width: number, height: number, format: ImageFormat) {
        try {
            await this.shade.toCaptureMode();
            await this.contour.toCaptureMode(width);
            await this.wind.toCaptureMode(width);
            this.rasterizer.setSize(width, height);
            await this.rasterizer.render();
            return this.rasterizer.toBlob(format);
        } finally {
            await Promise.all([
                this.shade.toDisplayMode(),
                this.contour.toDisplayMode(),
                this.wind.toDisplayMode(),
            ]);
        }
    }
    // 動画生成の開始・終了。開始中はRasterizerがフレーム間の静的層キャッシュと
    // canvas直接合成のfast pathを使う (初回フレームで従来経路と画素照合)
    beginVideoCapture() {
        this.rasterizer.beginVideoCapture();
    }
    endVideoCapture() {
        this.rasterizer.endVideoCapture();
    }
    // 動画のフレーム用。現在表示をラスタライズした canvas を返す
    // (返る canvas は次回呼び出しで上書きされるため、即座にコピーして使うこと)。
    async toCanvas(width: number, height: number) {
        try {
            await this.shade.toCaptureMode();
            await this.contour.toCaptureMode(width);
            await this.wind.toCaptureMode(width);
            this.rasterizer.setSize(width, height);
            await this.rasterizer.render();
            return this.rasterizer.getCanvas();
        } finally {
            await Promise.all([
                this.shade.toDisplayMode(),
                this.contour.toDisplayMode(),
                this.wind.toDisplayMode(),
            ]);
        }
    }
    // 「画像生成」の静止画1枚。完成時に別タブのビューアで開く
    // (popupブロック時は従来のDLへフォールバック)
    async downloadImageFromUi() {
        this.disableButtons();
        this.link.textContent = "画像出力中...";
        try {
            const [width, height] = this.size.value.split("x").map(Number);
            const format = this.getSelectedFormat();
            const blob = await this.toBlob(width, height, format);
            this.revokeObjectUrl();
            this.objectUrl = URL.createObjectURL(blob);
            const filename = `${outputFilePrefix()}_${dayjs().format("YYYYMMDD_HHmmss")}.${format}`;
            this.link.textContent = "";
            this.link.appendChild(
                createViewerLink({
                    url: this.objectUrl,
                    filename,
                    kind: "image",
                    label: "画像完成",
                }),
            );
            // 完成時にビューアを自動で開く。生成に時間がかかりユーザー操作の
            // 有効期間 (約5秒) を過ぎてpopupブロックされた場合は、上の
            // 完成リンクのクリックで開いてもらう (自動DLへは落とさない)
            openViewer({ url: this.objectUrl, filename, kind: "image" });
        } catch (error) {
            const message =
                error instanceof Error ? error.message : String(error);
            this.link.textContent = `画像出力失敗: ${message}`;
            console.error(error);
        } finally {
            this.enableButtons();
        }
    }
    private disableButtons() {
        this.imageButton.setAttribute("disabled", "");
    }
    private enableButtons() {
        this.imageButton.removeAttribute("disabled");
    }
    private revokeObjectUrl() {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
    private updateCropGuide() {
        const display = this.size.value === "1440x1080" ? "inline" : "none";
        const guide = document.querySelector<SVGGElement>("#out_sq");
        guide?.setAttribute("display", display);
        if (guide) {
            guide.style.display = display;
        }
    }
    private updateSafetyGuide() {
        const display = document.querySelector<HTMLInputElement>("#cb_safety")
            ?.checked
            ? "inline"
            : "none";
        const guide = document.querySelector<SVGGElement>("#safety");
        guide?.setAttribute("display", display);
        if (guide) {
            guide.style.display = display;
        }
    }
}

// 動画fast pathで扱えないDOM状態 (旧一枚PNG表示・未知のclip形状など)。
// 捕捉したら従来のフルserialize経路へ自動フォールバックする
class FastPathUnsupported extends Error {}

const SVG_NS = "http://www.w3.org/2000/svg";

class Rasterizer {
    private static readonly MAX_OUTPUT_WIDTH = 3840;
    private static readonly MAX_OUTPUT_HEIGHT = 2160;
    private static readonly MAX_OUTPUT_PIXELS =
        Rasterizer.MAX_OUTPUT_WIDTH * Rasterizer.MAX_OUTPUT_HEIGHT;
    private readonly svg_main = document.querySelector("#svg_main")!;
    private readonly svg_over = document.querySelector("#svg_over")!;
    private readonly canvas_main = document.createElement("canvas")!;
    private readonly canvas_over = document.createElement("canvas")!;
    private readonly canvas_output = document.createElement("canvas")!;
    private ctx_main: CanvasRenderingContext2D;
    private ctx_over: CanvasRenderingContext2D;
    private outputWidth = 1920;
    private outputHeight = 1080;
    private renderWidth = 1920;
    private renderHeight = 1080;
    private fontDataUrl: Promise<string> | null = null;
    // svg_overは動画のフレーム間でほぼ静的 (矢羽根などが変わればDOM変異が起きる)。
    // 変異がない間はラスタライズ結果のcanvas_overを再利用し、フレーム毎の
    // serialize+フォント埋め込み+decodeを省く (canvas画素の変化はDOM変異に
    // ならないが、svg_overにcanvasはない)
    private overDirty = true;
    // FT変更時は同値のattribute書き込みが多数走るため、実際に値が変わった
    // 変異だけを変更として扱う (oldValueと現在値の比較)
    private readonly overObserver = new MutationObserver((records) =>
        this.processOverRecords(records),
    );
    private processOverRecords(records: MutationRecord[]) {
        for (const record of records) {
            if (!Rasterizer.isEffectiveChange(record)) {
                continue;
            }
            this.overDirty = true;
            if (!this.hasOverDynamicAncestor(record.target)) {
                this.overStaticDirty = true;
            }
        }
    }
    // 保留中の変異recordを取り出してフラグへ反映する (捨てない)
    private drainOverRecords() {
        this.processOverRecords(this.overObserver.takeRecords());
    }
    private static isEffectiveChange(record: MutationRecord) {
        if (record.type === "attributes") {
            const target = record.target as Element;
            return (
                record.oldValue !==
                target.getAttribute(record.attributeName ?? "")
            );
        }
        if (record.type === "characterData") {
            return record.oldValue !== record.target.nodeValue;
        }
        return true;
    }
    // --- 動画フレーム用fast path ---
    // svg_main全体のserialize+decode (basemap数千path・主画像canvasのPNG化を含む)
    // をフレーム毎に行わず、フレーム間で不変な層を1回だけラスタライズして再利用し、
    // 主画像などのcanvasはPNGを経由せずdrawImageで直接合成する。
    // z順: 下地塗り → 主画像canvas(雲/主) → 等値線canvas → 地図線 → 風path → 粒子canvas。
    // 動画開始時の初回フレームで従来経路と画素比較し、不一致なら従来経路へ戻す
    private videoMode = false;
    private fastState: "unverified" | "ok" | "off" = "off";
    private staticDirty = true;
    private staticBelow: HTMLCanvasElement | null = null;
    private staticLines: HTMLCanvasElement | null = null;
    // svg_over側: FT依存の時計・日付 (#fortime) を境に下側/上側の静的層へ分け、
    // #fortimeだけを毎フレーム最小SVGで描く。矢羽根 (#arrows) 表示中は
    // FT毎に大きく変わるため従来のsvg_over丸ごと再描画へ落とす
    private overStaticDirty = true;
    private overStaticBelow: HTMLCanvasElement | null = null;
    private overStaticAbove: HTMLCanvasElement | null = null;
    private static readonly DYNAMIC_IDS = new Set([
        "gpv_tiles_cloud_foreign_object",
        "gpv_tiles_main_foreign_object",
        "contour",
        "wind",
        "particle_foreign_object",
    ]);
    private static readonly DYNAMIC_OVER_IDS = new Set(["fortime", "arrows"]);
    private readonly mainObserver = new MutationObserver((records) => {
        for (const record of records) {
            if (this.isStaticMutation(record)) {
                this.staticDirty = true;
                return;
            }
        }
    });
    constructor() {
        this.overObserver.observe(this.svg_over, {
            subtree: true,
            attributes: true,
            attributeOldValue: true,
            childList: true,
            characterData: true,
            characterDataOldValue: true,
        });
    }
    beginVideoCapture() {
        this.videoMode = true;
        this.fastState = "unverified";
        this.staticDirty = true;
        this.overStaticDirty = true;
        this.mainObserver.observe(this.svg_main, {
            subtree: true,
            attributes: true,
            childList: true,
            characterData: true,
        });
    }
    endVideoCapture() {
        this.videoMode = false;
        this.fastState = "off";
        this.mainObserver.disconnect();
        this.staticBelow = null;
        this.staticLines = null;
        this.overStaticBelow = null;
        this.overStaticAbove = null;
    }
    private hasOverDynamicAncestor(node: Node) {
        let element: Element | null =
            node instanceof Element ? node : node.parentElement;
        while (element) {
            if (Rasterizer.DYNAMIC_OVER_IDS.has(element.id)) {
                return true;
            }
            element = element.parentElement;
        }
        return false;
    }
    // 動的subtree (canvas層・風・粒子) 内だけの変異は静的層キャッシュへ影響しない
    private isStaticMutation(record: MutationRecord) {
        if (this.hasDynamicAncestor(record.target)) {
            // 動的subtree内の増減・属性変更 (風pathの入替、canvas属性など)
            return false;
        }
        if (record.type === "childList") {
            // 動的要素自身の挿し直し (foreignObject再挿入など) は静的層に無関係。
            // 削除済みnodeは親を辿れないため、動的root自身のidで判定する
            const nodes = [...record.addedNodes, ...record.removedNodes];
            return !nodes.every(
                (node) =>
                    node instanceof Element &&
                    Rasterizer.DYNAMIC_IDS.has(node.id),
            );
        }
        return true;
    }
    private hasDynamicAncestor(node: Node) {
        let element: Element | null =
            node instanceof Element ? node : node.parentElement;
        while (element) {
            if (Rasterizer.DYNAMIC_IDS.has(element.id)) {
                return true;
            }
            element = element.parentElement;
        }
        return false;
    }
    setSize(width: number, height: number) {
        if (
            !Number.isInteger(width) ||
            !Number.isInteger(height) ||
            width <= 0 ||
            height <= 0 ||
            width > Rasterizer.MAX_OUTPUT_WIDTH ||
            height > Rasterizer.MAX_OUTPUT_HEIGHT ||
            width * height > Rasterizer.MAX_OUTPUT_PIXELS
        ) {
            throw new Error("対応していない画像サイズです。");
        }
        this.outputWidth = width;
        this.outputHeight = height;
        this.renderWidth = Math.max(width, Math.round((height * 16) / 9));
        this.renderHeight = Math.round((this.renderWidth * 9) / 16);
        // width/heightの代入は同値でもcanvasをクリアするため、変更時だけ行う
        // (動画のフレーム間でcanvas_overのキャッシュを保つ)
        if (
            this.canvas_main.width !== this.renderWidth ||
            this.canvas_main.height !== this.renderHeight
        ) {
            this.canvas_main.width = this.renderWidth;
            this.canvas_main.height = this.renderHeight;
            this.ctx_main = this.canvas_main.getContext("2d")!;
            this.canvas_over.width = this.renderWidth;
            this.canvas_over.height = this.renderHeight;
            this.ctx_over = this.canvas_over.getContext("2d")!;
            this.overDirty = true;
        }
        const needsCrop =
            width !== this.renderWidth || height !== this.renderHeight;
        // 16:9では合成済みcanvas_mainを直接出力し、canvasの重複確保を避ける。
        this.canvas_output.width = needsCrop ? width : 1;
        this.canvas_output.height = needsCrop ? height : 1;
    }
    async render() {
        await document.fonts.ready;
        await document.fonts.load('700 16px "GenEiGothicP-Bold"');
        if (this.videoMode && this.fastState === "unverified") {
            // 動画の初回フレーム: 従来経路の結果を正としつつ、fast pathの結果と
            // 画素比較して以後の採否を決める
            await this.renderSlowComposite();
            try {
                const probe = document.createElement("canvas");
                probe.width = this.renderWidth;
                probe.height = this.renderHeight;
                await this.renderFastComposite(probe.getContext("2d")!);
                const mismatch = this.compareCanvases(this.canvas_main, probe);
                this.fastState = mismatch <= 0.01 ? "ok" : "off";
                console.info(
                    `動画fast path: ${this.fastState} (画素不一致率 ${(mismatch * 100).toFixed(3)}%)`,
                );
            } catch (error) {
                this.fastState = "off";
                console.info("動画fast path非対応の表示状態:", error);
            }
        } else if (this.videoMode && this.fastState === "ok") {
            try {
                await this.renderFastComposite(this.ctx_main);
            } catch (error) {
                console.info("動画fast pathを無効化して従来経路へ:", error);
                this.fastState = "off";
                await this.renderSlowComposite();
            }
        } else {
            await this.renderSlowComposite();
        }
        if (
            this.outputWidth !== this.renderWidth ||
            this.outputHeight !== this.renderHeight
        ) {
            const context = this.canvas_output.getContext("2d")!;
            context.clearRect(0, 0, this.outputWidth, this.outputHeight);
            const sourceX = (this.renderWidth - this.outputWidth) / 2;
            const sourceY = (this.renderHeight - this.outputHeight) / 2;
            context.drawImage(
                this.canvas_main,
                sourceX,
                sourceY,
                this.outputWidth,
                this.outputHeight,
                0,
                0,
                this.outputWidth,
                this.outputHeight,
            );
        }
    }
    // 従来経路: svg_main全体serialize + svg_over (変更検知キャッシュ) の合成
    private async renderSlowComposite() {
        await this.renderSvg(this.svg_main, this.ctx_main);
        await this.renderOverSlow();
        this.ctx_main.drawImage(this.canvas_over, 0, 0);
    }
    private async renderOverSlow() {
        if (!this.overDirty) {
            return;
        }
        // 保留中の変異を先に反映してからフラグを消す。ここからのserializeは
        // 現在のDOMを取り込むため、以前の変異は捨ててよい
        this.drainOverRecords();
        this.overDirty = false;
        try {
            await this.renderSvg(this.svg_over, this.ctx_over);
        } catch (error) {
            this.overDirty = true;
            throw error;
        }
    }
    // fast path: svg_main層のcanvas直接合成 + svg_over層の静的分割合成
    private async renderFastComposite(context: CanvasRenderingContext2D) {
        await this.renderFastFrame(context);
        await this.renderOverFastInto(context);
    }
    // 従来経路とfast pathの画素差 (成分差>32の画素の割合) を返す
    private compareCanvases(a: HTMLCanvasElement, b: HTMLCanvasElement) {
        const da = a
            .getContext("2d")!
            .getImageData(0, 0, this.renderWidth, this.renderHeight).data;
        const db = b
            .getContext("2d")!
            .getImageData(0, 0, this.renderWidth, this.renderHeight).data;
        let bad = 0;
        for (let i = 0; i < da.length; i += 4) {
            if (
                Math.abs(da[i] - db[i]) > 32 ||
                Math.abs(da[i + 1] - db[i + 1]) > 32 ||
                Math.abs(da[i + 2] - db[i + 2]) > 32 ||
                Math.abs(da[i + 3] - db[i + 3]) > 32
            ) {
                bad++;
            }
        }
        return bad / (da.length / 4);
    }
    private async renderFastFrame(context: CanvasRenderingContext2D) {
        this.assertFastSupported();
        await this.ensureStaticLayers();
        context.clearRect(0, 0, this.renderWidth, this.renderHeight);
        context.drawImage(this.staticBelow!, 0, 0);
        this.drawCanvasLayer(context, "gpv_tiles_cloud_foreign_object");
        this.drawCanvasLayer(context, "gpv_tiles_main_foreign_object");
        this.drawContourLayer(context);
        context.drawImage(this.staticLines!, 0, 0);
        await this.drawWindOverlay(context);
        await this.drawParticleLayer(context);
    }
    private assertFastSupported() {
        // 旧一枚PNG表示 (region_imageが可視でhrefを持つ) はフレーム毎にhrefが
        // 変わり静的層へ入れられないため、従来経路で処理する
        for (const image of this.svg_main.querySelectorAll(
            "#screen_rotate > image",
        )) {
            const href =
                image.getAttribute("href") ??
                image.getAttribute("xlink:href") ??
                image.getAttributeNS("http://www.w3.org/1999/xlink", "href");
            if (href && this.isRendered(image, this.svg_main)) {
                throw new FastPathUnsupported("旧一枚PNG表示中");
            }
        }
    }
    private async ensureStaticLayers() {
        if (
            !this.staticDirty &&
            this.staticBelow &&
            this.staticBelow.width === this.renderWidth &&
            this.staticBelow.height === this.renderHeight
        ) {
            return;
        }
        // フラグはclone前に消す。ラスタライズ中の変異は次フレームで再構築する
        this.staticDirty = false;
        this.mainObserver.takeRecords();
        try {
            this.staticBelow = await this.rasterizeStatic("below");
            this.staticLines = await this.rasterizeStatic("lines");
        } catch (error) {
            this.staticDirty = true;
            throw error;
        }
    }
    private async rasterizeStatic(stratum: "below" | "lines") {
        const clone = this.svg_main.cloneNode(true) as SVGSVGElement;
        clone.setAttribute("width", String(this.renderWidth));
        clone.setAttribute("height", String(this.renderHeight));
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
        clone.querySelector("#particle_foreign_object")?.remove();
        const rotate = clone.querySelector("#screen_rotate");
        const screen = clone.querySelector(":scope > svg");
        if (!rotate || !screen) {
            throw new FastPathUnsupported("screen_rotateが見つからない");
        }
        if (stratum === "lines") {
            // 線の層には #screen より下に描かれるもの (背景g#backgroundなど) を
            // 入れない。残すと不透明背景が下層を塗り潰す
            for (const sibling of [...clone.children]) {
                if (sibling === screen) {
                    break;
                }
                const tag = sibling.tagName.toLowerCase();
                if (tag !== "style" && tag !== "defs") {
                    sibling.remove();
                }
            }
        }
        const children = [...rotate.children];
        const cloudIndex = children.findIndex(
            (child) => child.id === "gpv_tiles_cloud_foreign_object",
        );
        const contourIndex = children.findIndex(
            (child) => child.id === "contour",
        );
        if (cloudIndex < 0 || contourIndex < 0 || cloudIndex > contourIndex) {
            throw new FastPathUnsupported("層構造が想定と異なる");
        }
        children.forEach((child, index) => {
            const keep =
                stratum === "below"
                    ? index < cloudIndex
                    : index > contourIndex && child.id !== "wind";
            if (!keep) {
                child.remove();
            }
        });
        const source = await this.finishSerialize(clone);
        const canvas = document.createElement("canvas");
        canvas.width = this.renderWidth;
        canvas.height = this.renderHeight;
        await this.decodeSvgInto(source, canvas.getContext("2d")!);
        return canvas;
    }
    // foreignObject内canvasを座標変換行列でctxへ直接drawImageする
    private drawCanvasLayer(context: CanvasRenderingContext2D, foId: string) {
        const foreignObject =
            this.svg_main.querySelector<SVGForeignObjectElement>(`#${foId}`);
        if (
            !foreignObject ||
            getComputedStyle(foreignObject).display === "none"
        ) {
            return;
        }
        const canvas = foreignObject.querySelector("canvas");
        if (!canvas || canvas.width <= 1 || canvas.dataset.blank === "1") {
            return;
        }
        this.drawTransformedCanvas(
            context,
            canvas,
            foreignObject,
            foreignObject.getAttribute("clip-path"),
            canvas.dataset.smoothing !== "0",
        );
    }
    private drawContourLayer(context: CanvasRenderingContext2D) {
        const group = this.svg_main.querySelector("#contour");
        const foreignObject =
            this.svg_main.querySelector<SVGForeignObjectElement>(
                "#contour_tiles_foreign_object",
            );
        if (
            !group ||
            !foreignObject ||
            getComputedStyle(foreignObject).display === "none"
        ) {
            return;
        }
        const canvas = foreignObject.querySelector("canvas");
        if (!canvas || canvas.width <= 1) {
            return;
        }
        // クリップはグループ側 (モデルのデータ有効域) と foreignObject 側の両方を見る
        this.drawTransformedCanvas(
            context,
            canvas,
            foreignObject,
            group.getAttribute("clip-path") ??
                foreignObject.getAttribute("clip-path"),
            true,
        );
    }
    private drawTransformedCanvas(
        context: CanvasRenderingContext2D,
        canvas: HTMLCanvasElement,
        foreignObject: SVGForeignObjectElement,
        clipAttr: string | null,
        smoothing: boolean,
    ) {
        const matrix = this.outputMatrix(foreignObject);
        const x = Number(foreignObject.getAttribute("x") ?? "0");
        const y = Number(foreignObject.getAttribute("y") ?? "0");
        const width = Number(foreignObject.getAttribute("width") ?? "0");
        const height = Number(foreignObject.getAttribute("height") ?? "0");
        if (!(width > 0) || !(height > 0)) {
            return;
        }
        if (foreignObject.getAttribute("filter")) {
            throw new FastPathUnsupported("filter付きforeignObject");
        }
        context.save();
        try {
            context.setTransform(matrix);
            this.applyClipAttr(context, clipAttr);
            context.imageSmoothingEnabled = smoothing;
            context.imageSmoothingQuality = "high";
            context.drawImage(canvas, x, y, width, height);
        } finally {
            context.restore();
            context.setTransform(1, 0, 0, 1, 0, 0);
        }
    }
    // 要素のuser座標 → 出力canvas画素の変換行列
    private outputMatrix(
        element: SVGGraphicsElement,
        root: Element = this.svg_main,
    ) {
        const svg = root as SVGSVGElement;
        const rootCtm = svg.getScreenCTM();
        const elementCtm = element.getScreenCTM();
        const viewWidth = svg.viewBox.baseVal.width;
        if (!rootCtm || !elementCtm || !(viewWidth > 0)) {
            throw new FastPathUnsupported("座標変換行列を取得できない");
        }
        const scale = this.renderWidth / viewWidth;
        return new DOMMatrix([scale, 0, 0, scale, 0, 0])
            .multiply(rootCtm.inverse())
            .multiply(elementCtm);
    }
    // clip-path="url(#id)" をPath2Dへ変換してctx.clipする (現在の変換の下で)
    private applyClipAttr(
        context: CanvasRenderingContext2D,
        attr: string | null,
    ) {
        if (!attr || attr === "none") {
            return;
        }
        const match = /^url\(["']?#([^"')]+)["']?\)$/.exec(attr.trim());
        if (!match) {
            throw new FastPathUnsupported(`未対応のclip-path: ${attr}`);
        }
        const clip = document.getElementById(match[1]);
        if (!clip || clip.tagName.toLowerCase() !== "clippath") {
            throw new FastPathUnsupported(`clipPathが見つからない: ${attr}`);
        }
        if (clip.getAttribute("clipPathUnits") === "objectBoundingBox") {
            throw new FastPathUnsupported("objectBoundingBoxクリップ");
        }
        const path = new Path2D();
        let added = false;
        const addGeometry = (element: Element, extra?: DOMMatrix) => {
            const local = (element as unknown as SVGGraphicsElement).transform
                ?.baseVal;
            const own = local?.numberOfItems
                ? DOMMatrix.fromMatrix(local.consolidate()!.matrix)
                : new DOMMatrix();
            const matrix = extra ? extra.multiply(own) : own;
            const tag = element.tagName.toLowerCase();
            if (tag === "path") {
                const d = element.getAttribute("d");
                if (d) {
                    path.addPath(new Path2D(d), matrix);
                    added = true;
                }
            } else if (tag === "rect") {
                const rect = new Path2D();
                rect.rect(
                    Number(element.getAttribute("x") ?? "0"),
                    Number(element.getAttribute("y") ?? "0"),
                    Number(element.getAttribute("width") ?? "0"),
                    Number(element.getAttribute("height") ?? "0"),
                );
                path.addPath(rect, matrix);
                added = true;
            } else if (tag === "polygon") {
                const points = (element.getAttribute("points") ?? "")
                    .trim()
                    .split(/[\s,]+/)
                    .map(Number);
                if (points.length >= 6) {
                    const poly = new Path2D();
                    poly.moveTo(points[0], points[1]);
                    for (let i = 2; i < points.length; i += 2) {
                        poly.lineTo(points[i], points[i + 1]);
                    }
                    poly.closePath();
                    path.addPath(poly, matrix);
                    added = true;
                }
            } else if (tag === "use") {
                const href =
                    element.getAttribute("href") ??
                    element.getAttribute("xlink:href");
                const target = href?.startsWith("#")
                    ? document.getElementById(href.slice(1))
                    : null;
                if (!target) {
                    throw new FastPathUnsupported("clip内useを解決できない");
                }
                const shift = new DOMMatrix().translate(
                    Number(element.getAttribute("x") ?? "0"),
                    Number(element.getAttribute("y") ?? "0"),
                );
                addGeometry(target, matrix.multiply(shift));
            } else if (tag === "g") {
                for (const child of element.children) {
                    addGeometry(child, matrix);
                }
            } else {
                throw new FastPathUnsupported(`clip内の未対応要素: ${tag}`);
            }
        };
        for (const child of clip.children) {
            addGeometry(child);
        }
        if (added) {
            context.clip(path);
        }
    }
    // 風ベクトル (SVG path群) はフレーム毎に変わるため、その部分だけの
    // 最小SVGをserializeして重ねる。basemapや主画像を含まないので軽い
    private async drawWindOverlay(context: CanvasRenderingContext2D) {
        const wind = this.svg_main.querySelector("#wind");
        if (
            !wind ||
            wind.children.length === 0 ||
            !this.isRendered(wind, this.svg_main)
        ) {
            return;
        }
        const root = this.buildOverlayRoot(this.svg_main);
        const chain = this.cloneAncestorChain(wind, root, this.svg_main);
        chain.appendChild(wind.cloneNode(true));
        this.embedReferencedDefs(root);
        const source = await this.finishSerialize(root);
        await this.decodeSvgInto(source, context, false);
    }
    private async drawParticleLayer(context: CanvasRenderingContext2D) {
        const foreignObject =
            this.svg_main.querySelector<SVGForeignObjectElement>(
                "#particle_foreign_object",
            );
        if (
            !foreignObject ||
            getComputedStyle(foreignObject).display === "none"
        ) {
            return;
        }
        const canvas = foreignObject.querySelector("canvas");
        if (!canvas || canvas.width <= 1) {
            return;
        }
        if (!foreignObject.getAttribute("filter")) {
            this.drawTransformedCanvas(
                context,
                canvas,
                foreignObject,
                foreignObject.getAttribute("clip-path"),
                true,
            );
            return;
        }
        // 2D粒子はSVGのf_alpha filterが必要なため、canvasをdataURL化した
        // 最小SVG経由で重ねる (WebGL版はfilterなしで上の直接drawImageになる)
        const root = this.buildOverlayRoot(this.svg_main);
        const image = document.createElementNS(SVG_NS, "image");
        for (const name of ["x", "y", "width", "height", "clip-path", "filter"]) {
            const value = foreignObject.getAttribute(name);
            if (value !== null) {
                image.setAttribute(name, value);
            }
        }
        image.setAttribute("preserveAspectRatio", "none");
        image.setAttribute("href", canvas.toDataURL("image/png"));
        root.appendChild(image);
        this.embedReferencedDefs(root);
        const source = await this.finishSerialize(root);
        await this.decodeSvgInto(source, context, false);
    }
    // svg_over層のfast path: #fortime (時計・日付) を境とした2枚の静的層 +
    // #fortimeだけの最小SVGでz順を保ったまま合成する
    private async renderOverFastInto(context: CanvasRenderingContext2D) {
        const fortime = this.svg_over.querySelector("#fortime");
        const arrows = this.svg_over.querySelector("#arrows");
        // #arrowsには空のコンテナg (arrows_a/arrows_v) が常在するため、
        // 実描画要素 (g以外の子孫) の有無で判定する
        const arrowsActive =
            arrows !== null &&
            arrows.querySelector(":scope *:not(g)") !== null &&
            this.isRendered(arrows, this.svg_over);
        if (!fortime || arrowsActive) {
            // 矢羽根表示中はFT毎に#arrowsが変わるため丸ごと再描画する
            await this.renderOverSlow();
            context.drawImage(this.canvas_over, 0, 0);
            return;
        }
        await this.ensureOverStatic();
        context.drawImage(this.overStaticBelow!, 0, 0);
        if (this.isRendered(fortime, this.svg_over)) {
            // SVG経由 (フォント埋め込み+decode毎のフォント解析 ~85ms) を避け、
            // documentの読み込み済みフォントでcanvasへ直接描く
            await this.drawVectorSubtree(context, fortime);
        }
        context.drawImage(this.overStaticAbove!, 0, 0);
    }
    // rect / image / text (tspanなし) だけのSVG subtreeをcanvasへ直接描画する。
    // 対応外の要素・指定はFastPathUnsupportedで従来経路へ落とす。検証は
    // 動画初回フレームの従来経路との画素比較が兼ねる
    private async drawVectorSubtree(
        context: CanvasRenderingContext2D,
        root: Element,
    ) {
        const rootSvg = this.svg_over as SVGSVGElement;
        const tasks: (() => void)[] = [];
        const prepare = async (element: Element) => {
            const style = getComputedStyle(element);
            if (style.display === "none" || style.visibility === "hidden") {
                return;
            }
            const tag = element.tagName.toLowerCase();
            if (tag === "g") {
                if (style.opacity !== "1") {
                    throw new FastPathUnsupported("group opacity");
                }
                for (const child of element.children) {
                    await prepare(child);
                }
                return;
            }
            if (element.getAttribute("clip-path") || style.filter !== "none") {
                throw new FastPathUnsupported("clip/filter付き要素");
            }
            const matrix = this.outputMatrix(
                element as SVGGraphicsElement,
                rootSvg,
            );
            const opacity = Number(style.opacity);
            if (tag === "rect") {
                const x = Number(element.getAttribute("x") ?? "0");
                const y = Number(element.getAttribute("y") ?? "0");
                const width = Number(element.getAttribute("width") ?? "0");
                const height = Number(element.getAttribute("height") ?? "0");
                const rx = Number(element.getAttribute("rx") ?? "0");
                const fill = style.fill;
                const stroke = style.stroke;
                const fillOpacity = Number(style.fillOpacity);
                const strokeOpacity = Number(style.strokeOpacity);
                const strokeWidth = Number.parseFloat(style.strokeWidth);
                tasks.push(() => {
                    context.save();
                    context.setTransform(matrix);
                    context.beginPath();
                    context.roundRect(x, y, width, height, rx);
                    if (fill !== "none") {
                        context.globalAlpha = opacity * fillOpacity;
                        context.fillStyle = fill;
                        context.fill();
                    }
                    if (stroke !== "none") {
                        context.globalAlpha = opacity * strokeOpacity;
                        context.strokeStyle = stroke;
                        context.lineWidth = strokeWidth;
                        context.stroke();
                    }
                    context.restore();
                    context.setTransform(1, 0, 0, 1, 0, 0);
                });
                return;
            }
            if (tag === "image") {
                const href =
                    element.getAttribute("href") ??
                    element.getAttribute("xlink:href") ??
                    element.getAttributeNS(
                        "http://www.w3.org/1999/xlink",
                        "href",
                    );
                if (!href) {
                    return;
                }
                const image = await this.loadImage(
                    new URL(href, document.baseURI).href,
                );
                const x = Number(element.getAttribute("x") ?? "0");
                const y = Number(element.getAttribute("y") ?? "0");
                const width = Number(element.getAttribute("width") ?? "0");
                const height = Number(element.getAttribute("height") ?? "0");
                tasks.push(() => {
                    context.save();
                    context.setTransform(matrix);
                    context.globalAlpha = opacity;
                    context.drawImage(image, x, y, width, height);
                    context.restore();
                    context.setTransform(1, 0, 0, 1, 0, 0);
                });
                return;
            }
            if (tag === "text") {
                if (element.children.length > 0) {
                    throw new FastPathUnsupported("tspan等を含むtext");
                }
                const baseline = style.dominantBaseline;
                if (baseline !== "auto" && baseline !== "alphabetic") {
                    throw new FastPathUnsupported(
                        `dominant-baseline: ${baseline}`,
                    );
                }
                const content = element.textContent ?? "";
                if (content === "") {
                    return;
                }
                const anchorMap: Record<string, CanvasTextAlign> = {
                    start: "start",
                    middle: "center",
                    end: "end",
                };
                const align = anchorMap[style.textAnchor];
                if (!align) {
                    throw new FastPathUnsupported(
                        `text-anchor: ${style.textAnchor}`,
                    );
                }
                const letterSpacing =
                    style.letterSpacing === "normal"
                        ? "0px"
                        : style.letterSpacing;
                if (
                    letterSpacing !== "0px" &&
                    !("letterSpacing" in context)
                ) {
                    throw new FastPathUnsupported("letterSpacing非対応");
                }
                const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
                const x = Number(element.getAttribute("x") ?? "0");
                const y = Number(element.getAttribute("y") ?? "0");
                const fill = style.fill;
                const stroke = style.stroke;
                const fillOpacity = Number(style.fillOpacity);
                const strokeOpacity = Number(style.strokeOpacity);
                const strokeWidth = Number.parseFloat(style.strokeWidth);
                const lineJoin = style.strokeLinejoin as CanvasLineJoin;
                const lineCap = style.strokeLinecap as CanvasLineCap;
                const strokeFirst = style.paintOrder.startsWith("stroke");
                tasks.push(() => {
                    context.save();
                    context.setTransform(matrix);
                    context.font = font;
                    context.letterSpacing = letterSpacing;
                    context.textAlign = align;
                    context.textBaseline = "alphabetic";
                    const paintFill = () => {
                        if (fill === "none") {
                            return;
                        }
                        context.globalAlpha = opacity * fillOpacity;
                        context.fillStyle = fill;
                        context.fillText(content, x, y);
                    };
                    const paintStroke = () => {
                        if (stroke === "none") {
                            return;
                        }
                        context.globalAlpha = opacity * strokeOpacity;
                        context.strokeStyle = stroke;
                        context.lineWidth = strokeWidth;
                        context.lineJoin = lineJoin;
                        context.lineCap = lineCap;
                        context.strokeText(content, x, y);
                    };
                    if (strokeFirst) {
                        paintStroke();
                        paintFill();
                    } else {
                        paintFill();
                        paintStroke();
                    }
                    context.restore();
                    context.setTransform(1, 0, 0, 1, 0, 0);
                });
                return;
            }
            throw new FastPathUnsupported(`未対応要素: ${tag}`);
        };
        await prepare(root);
        for (const task of tasks) {
            task();
        }
    }
    private readonly imageCache = new Map<string, Promise<HTMLImageElement>>();
    private loadImage(url: string) {
        const cached = this.imageCache.get(url);
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            const image = new Image();
            image.src = url;
            await image.decode();
            return image;
        })();
        promise.catch(() => this.imageCache.delete(url));
        this.imageCache.set(url, promise);
        return promise;
    }
    private async ensureOverStatic() {
        if (
            !this.overStaticDirty &&
            this.overStaticBelow &&
            this.overStaticBelow.width === this.renderWidth &&
            this.overStaticBelow.height === this.renderHeight
        ) {
            return;
        }
        this.drainOverRecords();
        this.overStaticDirty = false;
        try {
            this.overStaticBelow = await this.rasterizeOverStatic("below");
            this.overStaticAbove = await this.rasterizeOverStatic("above");
        } catch (error) {
            this.overStaticDirty = true;
            throw error;
        }
    }
    private async rasterizeOverStatic(side: "below" | "above") {
        const clone = this.svg_over.cloneNode(true) as SVGSVGElement;
        clone.setAttribute("width", String(this.renderWidth));
        clone.setAttribute("height", String(this.renderHeight));
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
        for (const guide of clone.querySelectorAll("#safety, #out_sq")) {
            guide.setAttribute("display", "none");
            guide.setAttribute("style", "display:none");
        }
        const fortime = clone.querySelector("#fortime");
        if (!fortime) {
            throw new FastPathUnsupported("fortimeが見つからない");
        }
        // #fortimeから根まで遡り、各階層で描画順が逆側の兄弟を落とす
        let node: Element = fortime;
        while (node.parentElement) {
            const parent = node.parentElement;
            const siblings = [...parent.children];
            const position = siblings.indexOf(node);
            siblings.forEach((sibling, index) => {
                if (sibling === node) {
                    return;
                }
                const tag = sibling.tagName.toLowerCase();
                if (tag === "style" || tag === "defs" || tag === "desc") {
                    return;
                }
                if (side === "below" ? index > position : index < position) {
                    sibling.remove();
                }
            });
            node = parent;
        }
        fortime.remove();
        const source = await this.finishSerialize(clone, this.svg_over);
        const canvas = document.createElement("canvas");
        canvas.width = this.renderWidth;
        canvas.height = this.renderHeight;
        await this.decodeSvgInto(source, canvas.getContext("2d")!);
        return canvas;
    }
    // sourceと同じviewport/viewBoxを持つ空のオーバーレイ用ルート (style込み)
    private buildOverlayRoot(source: Element) {
        const root = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
        root.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        root.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
        root.setAttribute("width", String(this.renderWidth));
        root.setAttribute("height", String(this.renderHeight));
        const viewBox = source.getAttribute("viewBox");
        if (viewBox) {
            root.setAttribute("viewBox", viewBox);
        }
        for (const style of source.querySelectorAll(":scope > style")) {
            root.appendChild(style.cloneNode(true));
        }
        return root;
    }
    // 対象要素の祖先 (sourceRoot直下まで) を属性ごとshallow cloneで再現する
    private cloneAncestorChain(
        element: Element,
        root: SVGSVGElement,
        sourceRoot: Element,
    ) {
        const ancestors: Element[] = [];
        let current = element.parentElement;
        while (current && current !== sourceRoot) {
            ancestors.unshift(current);
            current = current.parentElement;
        }
        let parent: Element = root;
        for (const ancestor of ancestors) {
            const shallow = ancestor.cloneNode(false) as Element;
            shallow.removeAttribute("id");
            parent.appendChild(shallow);
            parent = shallow;
        }
        return parent;
    }
    // オーバーレイSVG内で参照される url(#...) の定義 (clipPath・filter等) を
    // 本体のDOMから複製して defs へ入れる。定義内の参照も再帰的に取り込む
    private embedReferencedDefs(root: SVGSVGElement) {
        const defs = document.createElementNS(SVG_NS, "defs");
        const included = new Set<string>();
        const collect = (scope: Element) => {
            const ids = new Set<string>();
            for (const element of [scope, ...scope.querySelectorAll("*")]) {
                for (const attr of element.attributes) {
                    const url = /url\(["']?#([^"')]+)["']?\)/.exec(attr.value);
                    if (url) {
                        ids.add(url[1]);
                    }
                    if (
                        (attr.name === "href" ||
                            attr.name === "xlink:href") &&
                        attr.value.startsWith("#")
                    ) {
                        ids.add(attr.value.slice(1));
                    }
                }
            }
            for (const id of ids) {
                if (included.has(id)) {
                    continue;
                }
                included.add(id);
                const definition = document.getElementById(id);
                if (!definition) {
                    continue;
                }
                const clone = definition.cloneNode(true) as Element;
                defs.appendChild(clone);
                collect(clone);
            }
        };
        collect(root);
        if (defs.children.length > 0) {
            root.insertBefore(defs, root.firstChild);
        }
    }
    private async decodeSvgInto(
        source: string,
        context: CanvasRenderingContext2D,
        clear = true,
    ) {
        const blob = new Blob([source], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        try {
            const image = new Image();
            image.src = url;
            await image.decode();
            if (clear) {
                context.clearRect(0, 0, this.renderWidth, this.renderHeight);
            }
            context.drawImage(image, 0, 0, this.renderWidth, this.renderHeight);
        } catch (error) {
            console.error(error);
            throw new Error("SVGをブラウザで画像化できませんでした。");
        } finally {
            URL.revokeObjectURL(url);
        }
    }
    private async renderSvg(svg: Element, context: CanvasRenderingContext2D) {
        const source = await this.serialize(svg);
        await this.decodeSvgInto(source, context);
    }
    private async serialize(svg: Element) {
        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute("width", String(this.renderWidth));
        clone.setAttribute("height", String(this.renderHeight));
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
        for (const guide of clone.querySelectorAll("#safety, #out_sq")) {
            guide.setAttribute("display", "none");
            guide.setAttribute("style", "display:none");
        }
        for (const canvas of clone.querySelectorAll("canvas")) {
            const source = document.querySelector<HTMLCanvasElement>(
                `#${canvas.id}`,
            );
            const foreignObject = canvas.closest("foreignObject");
            if (!source || !foreignObject) {
                continue;
            }
            // 非表示または全透明のcanvas (雲なし要素の雲層・OFF中の粒子など) は
            // PNG化せず除去する。大canvasのtoDataURLはフレーム毎の主要コストの
            // ひとつで、動画生成の高速化に効く
            const sourceFo = source.closest("foreignObject");
            const hidden =
                sourceFo !== null &&
                getComputedStyle(sourceFo as Element).display === "none";
            if (hidden || source.dataset.blank === "1") {
                foreignObject.remove();
                continue;
            }
            const image = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "image",
            );
            for (const name of [
                "x",
                "y",
                "width",
                "height",
                "clip-path",
                "filter",
            ]) {
                const value = foreignObject.getAttribute(name);
                if (value !== null) {
                    image.setAttribute(name, value);
                }
            }
            image.setAttribute("preserveAspectRatio", "none");
            image.setAttribute("href", source.toDataURL("image/png"));
            foreignObject.replaceWith(image);
        }
        return this.finishSerialize(clone, svg);
    }
    private async finishSerialize(clone: Element, liveRoot?: Element) {
        await this.embedFont(clone);
        await this.embedImages(clone, liveRoot ?? this.svg_main);
        return new XMLSerializer()
            .serializeToString(clone)
            .replace(/NS[0-9]+:href/g, "xlink:href");
    }
    private async embedFont(svg: Element) {
        // 文字のないSVG (日付・時計・地点名はsvg_over側) ではフォント埋め込みを
        // 省く。データURL化した658KB woffはBlobサイズとdecode時のフォント解析の
        // 両方でフレーム毎の主要コストになる
        if (!svg.querySelector("text")) {
            return;
        }
        const dataUrl = await this.getFontDataUrl();
        for (const style of svg.querySelectorAll("style")) {
            style.textContent =
                style.textContent?.replace(
                    /url\((['"]?)(?:\.\/)?font\/GenEiGothicP-Bold_sub3\.woff\1\)/g,
                    `url("${dataUrl}")`,
                ) ?? null;
        }
    }
    private getFontDataUrl() {
        if (!this.fontDataUrl) {
            const url = new URL(
                "font/GenEiGothicP-Bold_sub3.woff",
                document.baseURI,
            );
            this.fontDataUrl = this.fetchDataUrl(url.href, "フォント");
        }
        return this.fontDataUrl;
    }
    private async embedImages(clone: Element, originalSvg: Element) {
        for (const image of clone.querySelectorAll("image")) {
            const href =
                image.getAttribute("href") ??
                image.getAttribute("xlink:href") ??
                image.getAttributeNS("http://www.w3.org/1999/xlink", "href");
            if (!href || /^(?:data:|blob:|#)/.test(href)) {
                continue;
            }
            const original = image.id
                ? originalSvg.querySelector<Element>(`#${CSS.escape(image.id)}`)
                : null;
            if (original && !this.isRendered(original, originalSvg)) {
                image.removeAttribute("href");
                image.removeAttribute("xlink:href");
                continue;
            }
            const url = new URL(href, document.baseURI).href;
            image.setAttribute("href", await this.fetchDataUrl(url, "画像"));
            image.removeAttribute("xlink:href");
        }
    }
    private isRendered(element: Element, root: Element) {
        let current: Element | null = element;
        while (current) {
            const style = getComputedStyle(current);
            if (style.display === "none" || style.visibility === "hidden") {
                return false;
            }
            if (current === root) {
                break;
            }
            current = current.parentElement;
        }
        return true;
    }
    private readonly dataUrlCache = new Map<string, Promise<string>>();
    private fetchDataUrl(url: string, kind: string) {
        // 凡例PNGなどはURLが同じ限り再取得・再変換しない (動画フレーム毎に呼ばれる)
        const cached = this.dataUrlCache.get(url);
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(
                    `${kind}を取得できませんでした (${response.status})。`,
                );
            }
            return this.blobToDataUrl(await response.blob());
        })();
        promise.catch(() => this.dataUrlCache.delete(url));
        this.dataUrlCache.set(url, promise);
        return promise;
    }
    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () =>
                reject(new Error("画像資産を埋め込めませんでした。"));
            reader.readAsDataURL(blob);
        });
    }
    toBlob(format: string): Promise<Blob> {
        const canvas = this.prepareOutput(format);
        return new Promise((resolve, reject) =>
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("画像をBlobへ変換できませんでした。"));
                }
            }, `image/${format}`),
        );
    }
    toDataURL(format: string) {
        return this.prepareOutput(format).toDataURL(`image/${format}`);
    }
    getCanvas() {
        return this.prepareOutput("png");
    }
    private prepareOutput(format: string) {
        const source =
            this.outputWidth === this.renderWidth &&
            this.outputHeight === this.renderHeight
                ? this.canvas_main
                : this.canvas_output;
        if (format !== "jpeg") {
            return source;
        }
        const canvas = document.createElement("canvas");
        canvas.width = this.outputWidth;
        canvas.height = this.outputHeight;
        const context = canvas.getContext("2d")!;
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(source, 0, 0);
        return canvas;
    }
}
